import React, { useEffect } from 'react';
import { DatabaseIcon, LockIcon } from 'lucide-react';
import { StepProps, TITLE_PROFILE_MAP } from '../../types/onboarding';
const TITLES = Object.keys(TITLE_PROFILE_MAP);
const ROLES = ['CEO', 'VP of Sales', 'Division Lead', 'Team Lead', 'Loan Officer', 'Jr. Loan Officer', 'Processing Director', 'Processor Manager', 'Processor', 'Jr Processor', 'Solution Desk', 'IT', 'HR', 'Training'];
const DIVISION_LEADS = ['Marcus Rivera', 'Diana Chen', 'Robert Hayes'];
const TEAM_LEADS = ['Sarah Mitchell', 'James Park', 'Lisa Torres', 'Kevin Wright', 'Amy Foster'];
const BRANCHES = ['Ladera Ranch, CA', 'Irvine, CA', 'San Diego, CA', 'Los Angeles, CA', 'Phoenix, AZ', 'Dallas, TX', 'Houston, TX', 'Denver, CO', 'Seattle, WA', 'Miami, FL'];
const ENHANCED_NOTE_ROLES = ['Standard', 'Manager', 'Admin', 'Read Only'];
export function UserDetails({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  useEffect(() => {
    const isFormValid = !!(data.firstName.trim() && data.lastName.trim() && data.alias.trim() && data.username.trim() && data.email.trim() && data.title && data.role);
    setCanAdvance(isFormValid);
  }, [data, setCanAdvance]);
  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    const updates: Partial<typeof data> = {
      [field]: value
    };
    const first = field === 'firstName' ? value : data.firstName;
    const last = field === 'lastName' ? value : data.lastName;
    if (first && last && !data.alias) {
      updates.alias = `${first.charAt(0).toLowerCase()}${last.substring(0, 4).toLowerCase()}`;
    }
    updateData(updates);
  };
  const handleTitleChange = (value: string) => {
    updateData({
      title: value,
      profile: TITLE_PROFILE_MAP[value] || ''
    });
  };
  const accountName = data.firstName && data.lastName ? `${data.firstName} ${data.lastName} Database` : 'New User Database';
  const inputClass = 'w-full border-0 border-b border-gray-300 rounded-none px-0 py-2 text-sm focus:outline-none focus:border-[#2563EB] focus:ring-0 bg-transparent transition-colors';
  const selectClass = `${inputClass} appearance-none cursor-pointer`;
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1';
  const SelectChevron = () => <div className="pointer-events-none absolute inset-y-0 right-0 top-5 flex items-center px-2 text-gray-400" data-id="element-374">
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-375">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-376" />
      </svg>
    </div>;
  const SectionHeader = ({
    title
  }: {
    title: string;
  }) => <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-5 mt-8" data-id="element-377">
      {title}
    </h3>;
  return <div className="space-y-2" data-id="element-378">
      {/* Identity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-379">
        <div data-id="element-380">
          <label className={labelClass} data-id="element-381">
            First Name <span className="text-red-500" data-id="element-382">*</span>
          </label>
          <input type="text" value={data.firstName} onChange={e => handleNameChange('firstName', e.target.value)} className={inputClass} placeholder="Jane" data-id="element-383" />
        </div>
        <div data-id="element-384">
          <label className={labelClass} data-id="element-385">
            Last Name <span className="text-red-500" data-id="element-386">*</span>
          </label>
          <input type="text" value={data.lastName} onChange={e => handleNameChange('lastName', e.target.value)} className={inputClass} placeholder="Doe" data-id="element-387" />
        </div>
        <div data-id="element-388">
          <label className={labelClass} data-id="element-389">
            Alias <span className="text-red-500" data-id="element-390">*</span>
          </label>
          <input type="text" value={data.alias} onChange={e => updateData({
          alias: e.target.value
        })} maxLength={8} className={inputClass} placeholder="jdoe" data-id="element-391" />
        </div>
        <div data-id="element-392">
          <label className={labelClass} data-id="element-393">
            Email <span className="text-red-500" data-id="element-394">*</span>
          </label>
          <input type="email" value={data.email} onChange={e => {
          const email = e.target.value;
          const updates: Partial<typeof data> = {
            email
          };
          if (!data.username || data.username === data.email) {
            updates.username = email;
          }
          updateData(updates);
        }} className={inputClass} placeholder="jane.doe@rapidhomelending.com" data-id="element-395" />
        </div>
        <div className="md:col-span-2" data-id="element-396">
          <label className={labelClass} data-id="element-397">
            Username <span className="text-red-500" data-id="element-398">*</span>
          </label>
          <input type="email" value={data.username} onChange={e => updateData({
          username: e.target.value
        })} className={inputClass} placeholder="jane.doe@truss.com.prod" data-id="element-399" />
        </div>
      </div>

      {/* Role & Profile */}
      <SectionHeader title="Role & Profile" data-id="element-400" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-401">
        <div className="relative" data-id="element-402">
          <label className={labelClass} data-id="element-403">
            Title <span className="text-red-500" data-id="element-404">*</span>
          </label>
          <select value={data.title} onChange={e => handleTitleChange(e.target.value)} className={selectClass} data-id="element-405">
            <option value="" disabled data-id="element-406">
              Select Title
            </option>
            {TITLES.map(t => <option key={t} value={t} data-id="element-407">
                {t}
              </option>)}
          </select>
          <SelectChevron data-id="element-408" />
        </div>
        <div data-id="element-409">
          <label className={labelClass} data-id="element-410">Profile</label>
          <div className="flex items-center border-b border-gray-200 bg-gray-50 rounded-t-md px-3 py-2" data-id="element-411">
            <LockIcon className="w-3.5 h-3.5 text-gray-400 mr-2 flex-shrink-0" data-id="element-412" />
            <span className={`text-sm ${data.profile ? 'text-gray-700 font-medium' : 'text-gray-400 italic'}`} data-id="element-413">
              {data.profile || 'Auto-assigned from title'}
            </span>
          </div>
        </div>
        <div className="relative" data-id="element-414">
          <label className={labelClass} data-id="element-415">
            Role <span className="text-red-500" data-id="element-416">*</span>
          </label>
          <select value={data.role} onChange={e => updateData({
          role: e.target.value
        })} className={selectClass} data-id="element-417">
            <option value="" disabled data-id="element-418">
              Select Role
            </option>
            {ROLES.map(r => <option key={r} value={r} data-id="element-419">
                {r}
              </option>)}
          </select>
          <SelectChevron data-id="element-420" />
        </div>
      </div>

      {/* Organization */}
      <SectionHeader title="Organization" data-id="element-421" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-422">
        <div className="relative" data-id="element-423">
          <label className={labelClass} data-id="element-424">Division Lead</label>
          <select value={data.divisionLead} onChange={e => updateData({
          divisionLead: e.target.value
        })} className={selectClass} data-id="element-425">
            <option value="" data-id="element-426">Select Division Lead</option>
            {DIVISION_LEADS.map(d => <option key={d} value={d} data-id="element-427">
                {d}
              </option>)}
          </select>
          <SelectChevron data-id="element-428" />
        </div>
        <div className="relative" data-id="element-429">
          <label className={labelClass} data-id="element-430">Team Lead</label>
          <select value={data.teamLead} onChange={e => updateData({
          teamLead: e.target.value
        })} className={selectClass} data-id="element-431">
            <option value="" data-id="element-432">Select Team Lead</option>
            {TEAM_LEADS.map(t => <option key={t} value={t} data-id="element-433">
                {t}
              </option>)}
          </select>
          <SelectChevron data-id="element-434" />
        </div>
        <div className="relative" data-id="element-435">
          <label className={labelClass} data-id="element-436">Branch</label>
          <select value={data.branch} onChange={e => updateData({
          branch: e.target.value
        })} className={selectClass} data-id="element-437">
            <option value="" data-id="element-438">Select Branch</option>
            {BRANCHES.map(b => <option key={b} value={b} data-id="element-439">
                {b}
              </option>)}
          </select>
          <SelectChevron data-id="element-440" />
        </div>
      </div>

      {/* Contact */}
      <SectionHeader title="Contact" data-id="element-441" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-442">
        <div data-id="element-443">
          <label className={labelClass} data-id="element-444">Phone</label>
          <input type="tel" value={data.phone} onChange={e => updateData({
          phone: e.target.value
        })} className={inputClass} placeholder="(555) 123-4567" data-id="element-445" />
        </div>
        <div data-id="element-446">
          <label className={labelClass} data-id="element-447">Mobile</label>
          <input type="tel" value={data.mobile} onChange={e => updateData({
          mobile: e.target.value
        })} className={inputClass} placeholder="(555) 987-6543" data-id="element-448" />
        </div>
      </div>

      {/* Additional Settings */}
      <SectionHeader title="Additional Settings" data-id="element-449" />
      <div className="space-y-6" data-id="element-450">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6" data-id="element-451">
          {/* NMLS Toggle */}
          <div data-id="element-452">
            <label className={labelClass} data-id="element-453">Does this user have an NMLS?</label>
            <div className="flex items-center mt-1.5" data-id="element-454">
              <button type="button" onClick={() => updateData({
              hasNMLS: !data.hasNMLS,
              nmlsNumber: data.hasNMLS ? '' : data.nmlsNumber
            })} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${data.hasNMLS ? 'bg-[#2563EB]' : 'bg-gray-200'}`} data-id="element-455">
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data.hasNMLS ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-456" />
              </button>
              <span className="ml-3 text-sm text-gray-600" data-id="element-457">
                {data.hasNMLS ? 'Yes' : 'No'}
              </span>
            </div>
            {data.hasNMLS && <input type="text" value={data.nmlsNumber} onChange={e => updateData({
            nmlsNumber: e.target.value
          })} className={`${inputClass} mt-3`} placeholder="NMLS Number" data-id="element-458" />}
          </div>

          {/* LendingPad User Id */}
          <div data-id="element-459">
            <label className={labelClass} data-id="element-460">LendingPad User Id</label>
            <input type="text" value={data.lendingPadUserId} onChange={e => updateData({
            lendingPadUserId: e.target.value
          })} className={inputClass} placeholder="LP-12345" data-id="element-461" />
          </div>

          {/* Enhanced Note Role */}
          <div className="relative" data-id="element-462">
            <label className={labelClass} data-id="element-463">Enhanced Note Role</label>
            <select value={data.enhancedNoteRole} onChange={e => updateData({
            enhancedNoteRole: e.target.value
          })} className={selectClass} data-id="element-464">
              <option value="" data-id="element-465">Select Role</option>
              {ENHANCED_NOTE_ROLES.map(r => <option key={r} value={r} data-id="element-466">
                  {r}
                </option>)}
            </select>
            <SelectChevron data-id="element-467" />
          </div>
        </div>
      </div>

      {/* Account Preview */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4 flex items-center space-x-4 border border-gray-100" data-id="element-468">
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100" data-id="element-469">
          <DatabaseIcon className="w-5 h-5 text-[#2563EB]" data-id="element-470" />
        </div>
        <div data-id="element-471">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5" data-id="element-472">
            Account Record to be created
          </p>
          <p className="text-sm font-semibold text-gray-900" data-id="element-473">{accountName}</p>
        </div>
      </div>
    </div>;
}